import { useEffect, useRef } from "react";
import type { RealmId, UserProfile } from "@chuzi/shared/types";
import { t } from "@chuzi/shared/config";
import { AddCreditsButton } from "./add-credits-button";
import { useCreateCreditsGate } from "../hooks/use-create-credits-gate";
import { useSpendingModal } from "../lib/spending-modal";

interface GeneratorEntryProps {
  realm: RealmId;
  user?: UserProfile | null;
  onCreateUpload: () => void;
  onGenerate: () => void;
  compact?: boolean;
  autoPromptCredits?: boolean;
}

const containerStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: 24,
  padding: 32,
  background: "radial-gradient(ellipse at center, rgba(15,20,40,0.95) 0%, rgba(4,7,13,1) 100%)",
  position: "relative",
  overflow: "hidden",
};

const buttonBase: React.CSSProperties = {
  flex: 1,
  maxWidth: 320,
  height: "50%",
  minHeight: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
  fontWeight: 700,
  fontFamily: "inherit",
  borderRadius: 14,
  cursor: "pointer",
  position: "relative",
  transition: "transform 0.2s ease, box-shadow 0.3s ease",
};

const generateBtnStyle = (compact: boolean): React.CSSProperties =>
  compact
    ? {
        flex: 1,
        padding: "8px 10px",
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "inherit",
        color: "#e0c8ff",
        background: "rgba(160,80,220,0.08)",
        border: "1px solid rgba(180,100,255,0.4)",
        borderRadius: 8,
        cursor: "pointer",
      }
    : {
        ...buttonBase,
        color: "#e0c8ff",
        background: "rgba(160,80,220,0.08)",
        border: "1.5px solid rgba(180,100,255,0.4)",
        boxShadow: "0 0 20px rgba(180,100,255,0.15), inset 0 0 30px rgba(180,100,255,0.05)",
      };

export function GeneratorEntry({
  realm,
  user = null,
  onCreateUpload,
  onGenerate,
  compact = false,
  autoPromptCredits = false,
}: GeneratorEntryProps) {
  const { openSpendingModal } = useSpendingModal();
  const { creditsNeeded, canAfford, loaded, refresh } = useCreateCreditsGate(user);
  const promptedRef = useRef(false);

  useEffect(() => {
    if (!autoPromptCredits || !loaded || promptedRef.current || user?.is_admin || canAfford) {
      return;
    }
    promptedRef.current = true;
    openSpendingModal({
      pool: "create",
      creditsNeeded,
      onSuccess: () => void refresh(),
    });
  }, [autoPromptCredits, loaded, canAfford, creditsNeeded, openSpendingModal, refresh, user?.is_admin]);

  const generateButton = (
    <AddCreditsButton
      realm={realm}
      pool="create"
      creditsNeeded={creditsNeeded}
      canAfford={canAfford}
      user={user}
      label={t(realm, "generator_generate")}
      variant={compact ? "default" : "hero"}
      onAction={onGenerate}
      onCreditsAdded={() => void refresh()}
      style={generateBtnStyle(compact)}
    />
  );

  if (compact) {
    return (
      <div style={{ display: "flex", gap: 8, width: "100%" }}>
        <button
          type="button"
          onClick={onCreateUpload}
          style={{
            flex: 1,
            padding: "8px 10px",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "inherit",
            color: "#c8e0ff",
            background: "rgba(80,140,220,0.08)",
            border: "1px solid rgba(100,170,255,0.4)",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          {t(realm, "generator_create_upload")}
        </button>
        {generateButton}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 30% 40%, rgba(100,160,255,0.04) 0%, transparent 60%), radial-gradient(circle at 70% 60%, rgba(180,100,255,0.03) 0%, transparent 50%)",
          pointerEvents: "none",
        }}
      />

      <button
        type="button"
        onClick={onCreateUpload}
        style={{
          ...buttonBase,
          color: "#c8e0ff",
          background: "rgba(80,140,220,0.08)",
          border: "1.5px solid rgba(100,170,255,0.4)",
          boxShadow: "0 0 20px rgba(100,170,255,0.15), inset 0 0 30px rgba(100,170,255,0.05)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.02)";
          e.currentTarget.style.boxShadow =
            "0 0 35px rgba(100,170,255,0.3), inset 0 0 40px rgba(100,170,255,0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow =
            "0 0 20px rgba(100,170,255,0.15), inset 0 0 30px rgba(100,170,255,0.05)";
        }}
      >
        {t(realm, "generator_create_upload")}
      </button>

      <AddCreditsButton
        realm={realm}
        pool="create"
        creditsNeeded={creditsNeeded}
        canAfford={canAfford}
        user={user}
        label={t(realm, "generator_generate")}
        variant="hero"
        onAction={onGenerate}
        onCreditsAdded={() => void refresh()}
        style={{
          ...buttonBase,
          color: "#e0c8ff",
          background: "rgba(160,80,220,0.08)",
          border: "1.5px solid rgba(180,100,255,0.4)",
          boxShadow: "0 0 20px rgba(180,100,255,0.15), inset 0 0 30px rgba(180,100,255,0.05)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.02)";
          e.currentTarget.style.boxShadow =
            "0 0 35px rgba(180,100,255,0.3), inset 0 0 40px rgba(180,100,255,0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow =
            "0 0 20px rgba(180,100,255,0.15), inset 0 0 30px rgba(180,100,255,0.05)";
        }}
      />

      <style>{`
        @keyframes chuzi-glow-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
